<script lang="ts">
  import { toggleFullscreen } from '../fullscreen.ts';
  import { patch } from '../game/store.ts';
  import { ui } from '../game/store.ts';
  import { game } from '../game/game.ts';
  import { t } from '../i18n.ts';

  let { onMenu }: { onMenu: () => void } = $props();

  let invTop = $derived($ui.portrait ? $ui.viewRect.y + $ui.viewRect.h + 12 : null);

  function itemClick(id: string) {
    if (dragMoved) return;
    const sel = $ui.selected;
    if (sel && sel !== id) game.combine(sel, id);
    else game.selectItem(id);
  }

  // Drag-and-drop (Maus): Gegenstand auf Gegenstand = kombinieren, auf Szene = anwenden
  let drag = $state<{ id: string; icon: string; x: number; y: number } | null>(null);
  let dragMoved = false;
  function dragStart(e: PointerEvent, id: string, icon: string) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const sx = e.clientX, sy = e.clientY;
    dragMoved = false;
    const mv = (ev: PointerEvent) => {
      if (!dragMoved && Math.hypot(ev.clientX - sx, ev.clientY - sy) < 8) return;
      dragMoved = true;
      drag = { id, icon, x: ev.clientX, y: ev.clientY };
    };
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', mv);
      window.removeEventListener('pointerup', up);
      if (dragMoved) {
        drag = null;
        const el = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
        const slot = el?.closest<HTMLElement>('[data-item]');
        if (slot && slot.dataset.item !== id) game.combine(id, slot.dataset.item!);
        else if (el?.tagName === 'CANVAS') game.dropItem(id, ev.clientX, ev.clientY);
        setTimeout(() => (dragMoved = false), 0);
      }
    };
    window.addEventListener('pointermove', mv);
    window.addEventListener('pointerup', up);
  }
  let questsOpen = $state(false);
  let hintOpen = $state(false);
  let hint = $state<ReturnType<typeof game.currentHint>>(null);
  /** Tastaturkürzel (Desktop): Esc Menü, H Hinweis, A Aufgaben, K Karte, Leertaste Hotspots, F Vollbild */
  function onKey(e: KeyboardEvent) {
    const el = e.target as HTMLElement | null;
    if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const u = $ui;
    if (u.quiz || u.dialog || u.guest || u.map || u.shop || u.show || u.ended) return;
    const k = e.key.toLowerCase();
    if (k === 'escape') onMenu();
    else if (k === 'h') toggleHint();
    else if (k === 'a') questsOpen = !questsOpen;
    else if (k === 'k' && game.chapter?.map) patch({ map: true });
    else if (k === ' ') game.showHotspots();
    else if (k === 'f') toggleFullscreen();
    else return;
    e.preventDefault();
  }

  function toggleHint() {
    hintOpen = !hintOpen;
    hint = game.currentHint();
  }
  function more() {
    game.moreHint();
    hint = game.currentHint();
  }
  function itemLook(e: Event, id: string) {
    e.preventDefault();
    game.lookItem(id);
  }
  let pressTimer = 0;
  function itemDown(e: PointerEvent, id: string) {
    if (e.pointerType === 'mouse') return;
    pressTimer = window.setTimeout(() => {
      pressTimer = 0;
      game.lookItem(id);
    }, 450);
  }
  function itemUp() {
    clearTimeout(pressTimer);
  }
</script>

<header class="top">
  <button class="btn icon" onclick={onMenu} aria-label={t('hud.menu')} data-testid="menu-btn">
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" /></svg>
  </button>
  <div class="place" aria-live="polite">{$ui.sceneName}</div>
  <button class="btn icon" onclick={toggleHint} aria-label={t('hud.hint')} aria-expanded={hintOpen} data-testid="hint-btn">
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-3.5 10.9c.6.5 1 1.2 1 2V16h5v-.1c0-.8.4-1.5 1-2A6 6 0 0 0 12 3z" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round" /></svg>
  </button>
  {#if game.chapter?.map}
    <button class="btn icon" onclick={() => patch({ map: true })} aria-label={t('map.title')} data-testid="map-btn">
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z M9 4v14 M15 6v14" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round" /></svg>
    </button>
  {/if}
  {#if $ui.time !== null}<span class="tod" data-testid="time-of-day" title={t('time.' + $ui.time)}>{['🌅', '☀️', '🌇', '🌙'][$ui.time]}</span>{/if}
  <button class="btn icon" onclick={() => (questsOpen = !questsOpen)} aria-label={t('hud.quests')} aria-expanded={questsOpen} data-testid="quests-btn">
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 4h11v16H7z M4 4h3v16H4" stroke="currentColor" stroke-width="2" fill="none" stroke-linejoin="round" /><path d="M10 9h5M10 13h5" stroke="currentColor" stroke-width="2" stroke-linecap="round" /></svg>
  </button>
  <button class="btn hs" onclick={() => game.showHotspots()} data-testid="show-hotspots" aria-label={t('hud.hotspots')}>
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3.2" fill="currentColor" /><circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="3 3" /></svg>
    <span class="lbl">{t('hud.hotspots')}</span>
  </button>
</header>

<svelte:window onkeydown={onKey} />

{#if questsOpen}
  <aside class="quests panel" data-testid="quests">
    <h2>{t('hud.quests')}</h2>
    {#if $ui.quests.length === 0}<p>{t('hud.noQuests')}</p>{/if}
    <ul>
      {#each $ui.quests.filter((x) => !x.side) as q}
        <li class:done={q.done}>{q.done ? '✓' : '○'} {q.text}</li>
      {/each}
    </ul>
    {#if $ui.quests.some((x) => x.side)}
      <h3>{t('hud.sideQuests')}</h3>
      <ul data-testid="side-quests">
        {#each $ui.quests.filter((x) => x.side) as q}
          <li class:done={q.done}>{q.done ? '✓' : '◇'} {q.text}</li>
        {/each}
      </ul>
    {/if}
  </aside>
{/if}

{#if hintOpen}
  <aside class="quests panel hint" data-testid="hint-panel">
    <h2>💡 {t('hud.hint')}</h2>
    {#if !hint}
      <p>{t('hud.noHint')}</p>
    {:else}
      <p class="qt">{hint.title}</p>
      <ol>
        {#each hint.hints as h, i}<li data-testid={'hint-' + i}><b>{t('hud.hintLevel.' + i)}:</b> {h}</li>{/each}
      </ol>
      {#if hint.level < 3}
        <button class="btn" onclick={more} data-testid="hint-more">{t('hud.hintMore.' + hint.level)}</button>
      {/if}
    {/if}
  </aside>
{/if}

{#if drag}
  <img class="ghost" src={drag.icon} alt="" style:left="{drag.x}px" style:top="{drag.y}px" />
{/if}

{#if $ui.hover}
  <div class="hover" style:left="{$ui.hover.x}px" style:top="{$ui.hover.y - 18}px" data-testid="hover-label">{$ui.hover.name}</div>
{/if}

<div class="inv" class:portrait={$ui.portrait} style:top={invTop !== null ? invTop + 'px' : null} role="toolbar" aria-label={t('hud.inventory')}>
  {#if $ui.inventory.length === 0}
    <span class="empty">{t('hud.inventoryEmpty')}</span>
  {/if}
  {#each $ui.inventory as it (it.id)}
    <button
      class="slot"
      class:sel={$ui.selected === it.id}
      title={it.name}
      aria-label={it.name}
      aria-pressed={$ui.selected === it.id}
      data-testid={'item-' + it.id}
      onclick={() => itemClick(it.id)}
      oncontextmenu={(e) => itemLook(e, it.id)}
      data-item={it.id}
      onpointerdown={(e) => {
        itemDown(e, it.id);
        dragStart(e, it.id, it.icon);
      }}
      onpointerup={itemUp}
      onpointerleave={itemUp}
    >
      <img src={it.icon} alt="" draggable="false" />
    </button>
  {/each}
  {#if $ui.selected}
    <span class="selhint">{t('hud.useItem', { item: $ui.inventory.find((i) => i.id === $ui.selected)?.name ?? '' })}</span>
  {/if}
</div>

<style>
  .top {
    position: fixed;
    top: calc(10px + var(--safe-top));
    left: calc(12px + var(--safe-left));
    right: calc(12px + var(--safe-right));
    display: flex;
    align-items: center;
    gap: 12px;
    z-index: 20;
    pointer-events: none;
  }
  .top > * {
    pointer-events: auto;
  }
  .place {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 1.15rem;
    color: var(--amber3);
    text-shadow: 0 2px 8px rgba(5, 8, 14, 0.9);
    pointer-events: none !important;
  }
  .hs {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    padding: 0.5em 1em;
  }
  @media (max-width: 520px) {
    .hs .lbl {
      display: none;
    }
    .hs {
      padding: 0;
      width: var(--touch);
      justify-content: center;
    }
  }
  .quests {
    position: fixed;
    top: calc(70px + var(--safe-top));
    right: calc(12px + var(--safe-right));
    width: min(320px, calc(100% - 24px));
    padding: 14px 16px;
    z-index: 26;
  }
  .quests h2 {
    margin: 0 0 6px;
    font-family: var(--font-display);
    color: var(--amber2);
    font-size: 1.1rem;
  }
  .quests ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 6px;
  }
  .hint .qt {
    font-weight: 700;
    margin: 0 0 6px;
  }
  .hint ol {
    padding-left: 18px;
    margin: 0 0 10px;
    display: grid;
    gap: 6px;
    line-height: 1.4;
  }
  .quests li.done {
    opacity: 0.55;
    text-decoration: line-through;
  }
  .ghost {
    position: fixed;
    width: 64px;
    height: 64px;
    transform: translate(-50%, -50%);
    pointer-events: none;
    z-index: 60;
    filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.6));
  }
  .hover {
    position: fixed;
    transform: translate(-50%, -100%);
    padding: 6px 12px;
    border-radius: 8px;
    background: rgba(12, 20, 34, 0.85);
    border: 1px solid var(--panel-edge);
    font-weight: 700;
    color: var(--amber3);
    pointer-events: none;
    white-space: nowrap;
    z-index: 25;
    font-size: 0.95rem;
  }
  .inv {
    position: fixed;
    left: 50%;
    transform: translateX(-50%);
    bottom: calc(12px + var(--safe-bottom));
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    min-height: 72px;
    max-width: calc(100% - 24px);
    border-radius: 18px;
    background: rgba(22, 36, 58, 0.78);
    border: 1.5px solid var(--panel-edge);
    box-shadow: var(--shadow);
    z-index: 20;
    flex-wrap: wrap;
    justify-content: center;
  }
  .inv.portrait {
    bottom: auto;
  }
  .empty {
    opacity: 0.6;
    font-size: 0.9rem;
    padding: 0 8px;
  }
  .slot {
    width: 58px;
    height: 58px;
    border-radius: 12px;
    border: 1.5px solid rgba(226, 194, 122, 0.3);
    background: radial-gradient(circle at 50% 40%, rgba(246, 200, 109, 0.18), rgba(12, 20, 34, 0.6));
    padding: 4px;
    touch-action: none;
  }
  .slot img {
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
  .slot.sel {
    border-color: var(--amber2);
    box-shadow: 0 0 0 3px rgba(246, 200, 109, 0.4), 0 0 18px rgba(246, 200, 109, 0.5);
  }
  .selhint {
    font-size: 0.85rem;
    color: var(--amber3);
    max-width: 220px;
  }
  .tod {
    font-size: 1.4rem;
    align-self: center;
  }
  /* schmale Handys: Tageszeit steht in der Stadtkarte; Knöpfe enger, Titel kleiner – alles bleibt im Bild */
  @media (max-width: 480px) {
    .tod {
      display: none;
    }
    .top {
      gap: 6px;
    }
    .place {
      font-size: 0.95rem;
    }
    .top :global(.btn.icon) {
      width: 44px;
      height: 44px;
      min-width: 44px;
      flex: none;
    }
  }
</style>
